import React from 'react'

import type { SnackbarAction, SnackbarKey } from 'notistack'
import { closeSnackbar, MaterialDesignContent, SnackbarProvider, useSnackbar } from 'notistack'
import { defineMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import { useAssertContext } from '@hooks/useAssertContext'

import { IconButton } from '@components/ui/Buttons'
import { CircleCheckRegularIcon, InfoIcon, WarningIcon, XmarkRegularIcon } from '@components/ui/svgIcons'

import type { AlertDispatchContextValue, AlertProviderProps, SetAlertFunction } from './types'

const HideMessage = defineMessage({
  id: 'buttons.alert.hide',
  defaultMessage: 'Bezár',
})

const SNACK_ALERT_DEFAULT_TIMEOUT = 5000

const IconWrapper = styled.div`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  position: absolute;
  left: 10px;
  top: 0;
  bottom: 0;
  margin: auto 0;
  display: flex;
  align-items: center;
  justify-content: center;
`

const CloseButton = styled(IconButton)`
  position: absolute;
  top: 12px;
  right: 6px;
`

const StyledMaterialDesignContent = styled(MaterialDesignContent)`
  max-width: 400px;
  padding: 6px 42px 6px 40px;
  flex-wrap: nowrap;
  word-break: break-word;
  position: relative;

  &.notistack-MuiContent-success {
    background-color: ${({ theme }) => theme.colors.success[40]};
  }
  &.notistack-MuiContent-error {
    background-color: ${({ theme }) => theme.colors.error[40]};
  }
  &.notistack-MuiContent-info {
    background-color: ${({ theme }) => theme.colors.blue[40]};
  }
`

const ICON_VARIANTS = {
  info: (
    <IconWrapper>
      <InfoIcon size={16} />
    </IconWrapper>
  ),
  error: (
    <IconWrapper>
      <WarningIcon size={20} />
    </IconWrapper>
  ),
  success: (
    <IconWrapper>
      <CircleCheckRegularIcon size={20} />
    </IconWrapper>
  ),
}

const COMPONENTS = {
  info: StyledMaterialDesignContent,
  error: StyledMaterialDesignContent,
  success: StyledMaterialDesignContent,
}

export const AlertDispatchContext = React.createContext<Nullable<AlertDispatchContextValue>>(null)
AlertDispatchContext.displayName = 'AlertDispatchContext'

function CustomAlertProvider({ children }: AlertProviderProps) {
  const { enqueueSnackbar } = useSnackbar()

  const setErrorAlert = React.useCallback<SetAlertFunction>(
    (message, { autoHideDuration = SNACK_ALERT_DEFAULT_TIMEOUT, persist = false, ...rest } = {}) => {
      enqueueSnackbar(message, {
        variant: 'error',
        autoHideDuration,
        persist,
        ...rest,
      })
    },
    [enqueueSnackbar]
  )

  const setInfoAlert = React.useCallback<SetAlertFunction>(
    (message, { autoHideDuration = SNACK_ALERT_DEFAULT_TIMEOUT, persist = false, ...rest } = {}) => {
      enqueueSnackbar(message, {
        variant: 'info',
        autoHideDuration,
        persist,
        ...rest,
      })
    },
    [enqueueSnackbar]
  )

  const setSuccessAlert = React.useCallback<SetAlertFunction>(
    (message, { autoHideDuration = SNACK_ALERT_DEFAULT_TIMEOUT, persist = false, ...rest } = {}) => {
      enqueueSnackbar(message, {
        variant: 'success',
        autoHideDuration,
        persist,
        ...rest,
      })
    },
    [enqueueSnackbar]
  )

  // due to dialog renders handled in the provider we need to memoize the provider values so it won't trigger
  // a rerender from all the consuming components
  const dispatchValue = React.useMemo<AlertDispatchContextValue>(
    () => ({ setErrorAlert, setInfoAlert, setSuccessAlert }),
    [setErrorAlert, setInfoAlert, setSuccessAlert]
  )

  return <AlertDispatchContext.Provider value={dispatchValue}>{children}</AlertDispatchContext.Provider>
}

export const AlertProvider = ({ children }: AlertProviderProps) => {
  const { formatMessage } = useIntl()

  const actionHandler: SnackbarAction = React.useCallback(
    (snackbarId?: SnackbarKey) => (
      <CloseButton
        onClick={() => closeSnackbar(snackbarId)}
        variant="primaryContainedTransparent"
        size="small"
        title={formatMessage(HideMessage)}
      >
        <XmarkRegularIcon size={12} />
      </CloseButton>
    ),
    [formatMessage]
  )

  return (
    <SnackbarProvider maxSnack={3} action={actionHandler} iconVariant={ICON_VARIANTS} Components={COMPONENTS}>
      <CustomAlertProvider>{children}</CustomAlertProvider>
    </SnackbarProvider>
  )
}

export function useAlertDispatch() {
  return useAssertContext(AlertDispatchContext)
}
